<template>
    <div>
        <ValidationObserver v-slot="{ handleSubmit }">
            <form @submit.prevent="handleSubmit(onSubmit)">
                <v-row>
                <v-col cols="12" md="6">
                    <h1>{{pet.name}}'s Medication details</h1>
                    <span class="subheading">If {{pet.name}} requires medication during their stay we require owners to complete this medication form prior to check-in</span>
                </v-col>
                </v-row>
                <v-row class="d-flex flex-column mt-5">
                    <v-col cols="12" md="6" class="py-0">
                        <text-input rules="required" placeholder="Name of the medication" name="Medication name" label="Medication name*" v-model="name"/>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                        <text-input rules="required" placeholder="What is this medication for?" name="Description" label="Description*" v-model="description"/>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                        <text-input rules="required" placeholder="e.g. 1 tablet or 0.5ml" name="Dose amount" label="Dose amount*" v-model="doseDetails"/>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                        <text-input placeholder="e.g. am, midday, pm" name="Dose time" label="Dose time (optional)" v-model="doseTime"/>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                        <text-input placeholder="e.g. daily, weekly on Mondays, on 23rd April" name="Dose frequency" label="Dose frequency (optional)" v-model="frequency"/>
                    </v-col>
                    <v-col cols="12" md="6" class="py-0">
                        <v-checkbox class="mt-0" label="Refrigeration required (optional)" v-model="refrigeration"/>
                    </v-col>
                </v-row>
                <v-row class="mb-0">
                <v-col cols="12" md="6" class="py-0">
                    <dropzone-upload
                        ref="dropzone"
                        :useCustomSlot="true"
                        :files.sync="files"
                        :loading.sync="uploading"
                        :options='{
                                    maxFilesize: 25,
                                    acceptedFiles: "image/*,application/pdf"
                                }'
                    >
                    <div style="color: rgba(0, 0, 0, 0.6)">
                      <v-icon class="mr-2">mdi-upload</v-icon> Click to upload a photo of the medication with instructions
                    </div>
                    </dropzone-upload>
                </v-col>
                </v-row>
                <v-row>
                <v-col cols="12" md="6">
                    <v-textarea
                        v-model="comment"
                        label="Comments (optional)"
                        auto-grow
                        outlined
                        rows="4"
                        hide-details
                    />
                </v-col>
                </v-row>
                <v-row>
                <v-col cols="12" md="6">
                    <v-btn color="success" block
                        :loading="loading"
                        :disabled="uploading"
                        type="submit"
                    >
                    Save
                    </v-btn>
                    <v-btn block
                        class="mt-3"
                    @click="$router.push({ name: 'MedicationList', params: {id: pet.uuid } })"
                    >
                    Back
                    </v-btn>
                </v-col>
                </v-row>
                
                <!-- Pet History Section -->
                <v-row>
                    <v-col cols="12">
                        <h2 class="mt-6">Pet History</h2>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-list>
                            <v-divider/>
                            <v-progress-linear
                                indeterminate
                                color="primary"
                                v-if="historyLoading"
                            />
                            <v-list-item-group>
                                <template v-for="(item, index) in petHistory">
                                    <v-list-item :key="item.id + '-' + item.type">
                                        <template v-slot:default>
                                            <v-list-item-content>
                                                <v-list-item-title>
                                                    <v-chip small :color="getHistoryTypeColor(item.type)" class="mr-2">{{ item.type }}</v-chip>
                                                    <strong>{{ getHistoryTitle(item) }}</strong>
                                                </v-list-item-title>
                                                <v-list-item-subtitle>
                                                    <strong>Added on:</strong> {{ formatDate(item.created_at) }}
                                                </v-list-item-subtitle>
                                                
                                                <!-- Vaccination specific fields -->
                                                <template v-if="item.type === 'vaccination'">
                                                    <v-list-item-subtitle v-if="item.comment">
                                                        <strong>Comment:</strong> {{ item.comment }}
                                                    </v-list-item-subtitle>
                                                    <v-list-item-subtitle v-if="item.files && item.files.length > 0">
                                                        <strong>Files:</strong>
                                                        <div v-for="(file, fileIndex) in item.files" :key="fileIndex">
                                                            <v-btn text small color="primary" @click="downloadFile(file.id, file.filename, item.id)">
                                                                <v-icon small class="mr-1">mdi-file-document-outline</v-icon>
                                                                {{ file.filename }}
                                                            </v-btn>
                                                        </div>
                                                    </v-list-item-subtitle>
                                                </template>
                                                
                                                <!-- Medication specific fields -->
                                                <template v-if="item.type === 'medication'">
                                                    <v-list-item-subtitle v-if="item.description">
                                                        <strong>Description:</strong> {{ item.description }}
                                                    </v-list-item-subtitle>
                                                    <v-list-item-subtitle v-if="item.dose_details">
                                                        <strong>Dose:</strong> {{ item.dose_details }}
                                                    </v-list-item-subtitle>
                                                    <v-list-item-subtitle v-if="item.time_of_day">
                                                        <strong>Time:</strong> {{ item.time_of_day }}
                                                    </v-list-item-subtitle>
                                                    <v-list-item-subtitle v-if="item.frequency">
                                                        <strong>Frequency:</strong> {{ item.frequency }}
                                                    </v-list-item-subtitle>
                                                    <v-list-item-subtitle v-if="item.comment">
                                                        <strong>Comment:</strong> {{ item.comment }}
                                                    </v-list-item-subtitle>
                                                    <v-list-item-subtitle v-if="item.files && item.files.length > 0">
                                                        <strong>Files:</strong>
                                                        <div v-for="(file, fileIndex) in item.files" :key="fileIndex">
                                                            <v-btn text small color="primary" @click="downloadFile(file.id, file.filename, item.id)">
                                                                <v-icon small class="mr-1">mdi-file-document-outline</v-icon>
                                                                {{ file.filename }}
                                                            </v-btn>
                                                        </div>
                                                    </v-list-item-subtitle>
                                                </template>
                                                
                                                <!-- Own Food specific fields -->
                                                <template v-if="item.type === 'ownfood'">
                                                    <v-list-item-subtitle v-if="item.food_type">
                                                        <strong>Food type:</strong> {{ item.food_type }}
                                                    </v-list-item-subtitle>
                                                    <v-list-item-subtitle v-if="item.measurement_per_meal">
                                                        <strong>Measurement per meal:</strong> {{ item.measurement_per_meal }}
                                                    </v-list-item-subtitle>
                                                    <v-list-item-subtitle v-if="item.feeding_time && item.feeding_time.length > 0">
                                                        <strong>Feeding time:</strong> {{ Array.isArray(item.feeding_time) ? item.feeding_time.join(', ') : item.feeding_time }}
                                                    </v-list-item-subtitle>
                                                </template>
                                            </v-list-item-content>
                                        </template>
                                    </v-list-item>
                                    <v-divider
                                        v-if="index + 1 < petHistory.length"
                                        :key="'div_' + index"
                                    />
                                </template>
                                <v-list-item v-if="petHistory.length === 0 && !historyLoading" key="no-data" class="text-center">
                                    <p class="ma-3 text-center">{{pet.name}} doesn't have any history records.</p>
                                </v-list-item>
                                <v-divider />
                            </v-list-item-group>
                        </v-list>
                        <v-btn
                            v-if="petHistory.length !== 0"
                            outlined
                            :loading="historyBtnLoading"
                            class="mx-4 mt-2 float-right"
                            :disabled="historyPage === historyLastPage"
                            @click="historyPage++"
                        >
                            Next
                        </v-btn>
                        <v-btn
                            v-if="petHistory.length !== 0"
                            outlined
                            :loading="historyBtnLoading"
                            class="mx-4 mt-2 float-left float-sm-right"
                            :disabled="historyPage === 1"
                            @click="historyPage--"
                        >
                            Previous
                        </v-btn>
                    </v-col>
                </v-row>
            </form>
        </ValidationObserver>
    </div>
  </template>
  
  <script>
  import { ValidationObserver } from "vee-validate";
  import { mapGetters} from "vuex";
  import DropzoneUpload from "@/components/DropzoneUpload";
  import PetsRepository from "@/api/PetsRepository";
  import FileUploadRepository from "@/api/FileUploadRepository";
  import TextInput from "../components/TextInputComponent.vue";
  import store from "@/store";
  import moment from 'moment';

  export default {
    title: 'Upload Medication Details',
    components: {
      DropzoneUpload,
      TextInput,
      ValidationObserver,
    },
    async beforeRouteEnter(to, from, next) {
        await store.dispatch("pet/get",to.params.id)
        next();
    },
    async beforeRouteUpdate(to, from, next) {
      await store.dispatch("pet/get",to.params.id)
      next();
    },
    data: () => ({
      comment: null,
      loading: false,
      files: [],
      uploading: false,
      name: '',
      description: '',
      selectedTime: '',
      doseDetails: '',
      refrigeration: '',
      frequency: '',
      doseTime: '',
      petHistory: [],
      historyPage: 1,
      historyLastPage: 1,
      historyLoading: false,
      historyBtnLoading: false,
    }),
    computed: {
      ...mapGetters({
        customer: 'auth/customer',
        pet: 'pet/pet',
      }),
    },
    mounted() {
      this.loadPetHistory();
    },
    methods: {
      formatDate(dateString) {
        return moment(dateString).format('MMMM D, YYYY [at] h:mm A');
      },
      getHistoryTypeColor(type) {
        switch(type) {
          case 'vaccination':
            return 'primary';
          case 'medication':
            return 'success';
          case 'ownfood':
            return 'warning';
          default:
            return 'grey';
        }
      },
      getHistoryTitle(item) {
        switch(item.type) {
          case 'vaccination':
            return 'Vaccination';
          case 'medication':
            return item.name || 'Medication';
          case 'ownfood':
            return item.brand_and_specifications || 'Own Food';
          default:
            return 'History Item';
        }
      },
      async downloadFile(fileId, filename, id) {
        try {
          const url = await FileUploadRepository.getSignedDownloadURL(fileId, id);
          // Create a temporary link element
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          link.setAttribute('target', '_blank');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (error) {
          console.error('Error downloading file:', error);
        }
      },
      async loadPetHistory() {
        this.historyLoading = true;
        this.historyBtnLoading = true;
        let response = await PetsRepository.getAllPetHistory(this.pet.uuid, this.historyPage);
        this.petHistory = response.data.data;
        this.historyLastPage = response.data.last_page;
        this.historyBtnLoading = false;
        this.historyLoading = false;
      },
      async onSubmit () {
        this.loading = true;
        let attachments = this.files.map(function (item) {
          return {
            temporaryFilename: item.temporaryFilename,
            filename: item.name,
            type: item.type,
          }
        })
        let medication = {
            attachments: attachments,
            comment: this.comment,
            name: this.name,
            description: this.description,
            dose_details: this.doseDetails,
            refrigeration: this.refrigeration,
            time_of_day: this.doseTime,
            frequency: this.frequency,
        }
        let response = await PetsRepository.uploadMedication(this.pet.uuid, medication, this.customer.properties[0].id)
        this.loading = false;
        if(response) {
          this.$router.push({ name: 'MedicationList' });
        }
      }
    },
    watch: {
      historyPage() {
        this.loadPetHistory();
      }
    },
  }
  </script>